'use client'

import { Button } from '@repo/ui/button'
import { Calendar } from '@repo/ui/calendar'
import { getLocale, useT } from '@repo/ui/hooks'
import { Popover, PopoverContent, PopoverTrigger } from '@repo/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/select'
import { cn, formatDate } from '@repo/ui/utils'
import { addDays, format, isBefore, isSameDay, startOfDay } from 'date-fns'
import { CalendarIcon } from 'lucide-react'
import { ReactNode, useEffect, useState } from 'react'

export function DatePickerWithPresets({
  children,
  dateHandler,
  isDisabled,
  disablePastDates = true,
  closePopoverOnSelect = false,
  canSetNoDate = true,
}: {
  children?: ReactNode
  dateHandler: {
    date: Date | undefined
    setDate: (date: Date | undefined) => void
  }
  canSetNoDate?: boolean
  isDisabled?: boolean
  disablePastDates?: boolean
  closePopoverOnSelect?: boolean
}) {
  const { tc } = useT()
  const now = new Date()
  const today = startOfDay(now) // Remove time component
  const dueDate = dateHandler.date ? startOfDay(dateHandler.date) : undefined
  const isToday = !!(dueDate && isSameDay(dueDate, today))
  const isTomorrow = !!(dueDate && isSameDay(dueDate, addDays(today, 1)))
  const isInAWeek = !!(dueDate && isSameDay(dueDate, addDays(today, 7)))
  const defaultValue = isToday ? '0' : isTomorrow ? '1' : undefined
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState<string | undefined>(defaultValue)
  useEffect(() => {
    if (!dueDate) setValue('undefined')
    else if (isToday) setValue('0')
    else if (isTomorrow) setValue('1')
    else if (isInAWeek) setValue('7')
    else setValue('x')
  }, [dueDate])
  return (
    <Popover onOpenChange={setOpen} open={open} defaultOpen={open}>
      <PopoverTrigger asChild>
        {children ? (
          children
        ) : (
          <Button
            className={cn(
              'w-[280px] justify-start text-left font-normal',
              !dateHandler.date && 'text-muted-foreground',
            )}
            disabled={isDisabled}
            variant={'outline'}
          >
            <CalendarIcon />
            {dateHandler.date ? (
              format(dateHandler.date, 'PPP', {
                locale: getLocale(),
              })
            ) : (
              <span>{tc('pickDate')}</span>
            )}
          </Button>
        )}
      </PopoverTrigger>
      {!isDisabled && (
        <PopoverContent className="flex w-auto flex-col space-y-2 p-2">
          <Select
            onValueChange={(value) => {
              if (value.includes('undefined')) dateHandler.setDate(undefined)
              else if (!value.includes('x'))
                dateHandler.setDate(addDays(new Date(), parseInt(value)))
            }}
            value={value}
            defaultValue={defaultValue}
          >
            <SelectTrigger className="font-medium">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent position="popper">
              {canSetNoDate && (
                <SelectItem value="undefined" className="data-[state=checked]:font-medium">
                  {tc(value === 'undefined' ? 'pickDate' : 'noDate', { cfl: true })}
                </SelectItem>
              )}
              <SelectItem value="0" className="data-[state=checked]:font-medium">
                {tc('today')}
              </SelectItem>
              <SelectItem value="1" className="data-[state=checked]:font-medium">
                {tc('tomorrow')}
              </SelectItem>
              <SelectItem value="7" className="data-[state=checked]:font-medium">
                {tc('inAWeek')}
              </SelectItem>
              {dueDate && !defaultValue && value === 'x' && (
                <SelectItem value="x" className="data-[state=checked]:font-medium">
                  {dueDate && formatDate(dueDate.toDateString())}
                </SelectItem>
              )}
            </SelectContent>
          </Select>
          <div className="rounded-md border">
            <Calendar
              hideNavigation
              showOutsideDays={false}
              captionLayout="dropdown"
              mode="single"
              selected={dateHandler.date}
              month={dateHandler.date}
              onMonthChange={(date) => {
                dateHandler.setDate(date)
              }}
              onSelect={(date) => {
                dateHandler.setDate(date)
                if (closePopoverOnSelect) setOpen(false)
              }}
              disabled={(date) => disablePastDates && isBefore(date, today)}
            />
          </div>
        </PopoverContent>
      )}
    </Popover>
  )
}
